import React from 'react';
import Icon from '../../../components/AppIcon';

const MatchScoreCard = ({ overallScore, skillsMatch, analysisData, role }) => {
  // Extract values from props
  const matchScore = overallScore || 0;
  const targetRole = role || 'Software Developer';
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-success/10';
    if (score >= 60) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Needs Improvement';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-subtle">
      <div className="space-y-6">
        {/* Dual Score Display */}
        <div className="grid grid-cols-2 gap-4">
          {/* Resume Quality Score */}
          <div className="text-center space-y-2">
            <div className={`inline-flex items-center justify-center w-28 h-28 rounded-full ${getScoreBgColor(matchScore)} border-4 ${getScoreColor(matchScore)} border-current`}>
              <div className="text-center">
                <div className={`text-3xl font-bold ${getScoreColor(matchScore)}`}>
                  {matchScore}%
                </div>
                <div className="text-xs text-muted-foreground">Quality</div>
              </div>
            </div>
            <div className={`text-sm font-semibold ${getScoreColor(matchScore)}`}>
              Resume Quality
            </div>
            <p className="text-xs text-muted-foreground">
              Overall resume strength
            </p>
          </div>

          {/* Job Match Score */}
          <div className="text-center space-y-2">
            <div className={`inline-flex items-center justify-center w-28 h-28 rounded-full ${getScoreBgColor(analysisData?.job_match_score || 0)} border-4 ${getScoreColor(analysisData?.job_match_score || 0)} border-current`}>
              <div className="text-center">
                <div className={`text-3xl font-bold ${getScoreColor(analysisData?.job_match_score || 0)}`}>
                  {analysisData?.job_match_score || 0}%
                </div>
                <div className="text-xs text-muted-foreground">Match</div>
              </div>
            </div>
            <div className={`text-sm font-semibold ${getScoreColor(analysisData?.job_match_score || 0)}`}>
              Job Match
            </div>
            <p className="text-xs text-muted-foreground">
              Match with {analysisData?.job_matches?.length || 0} jobs
            </p>
          </div>
        </div>

        {/* Target Role */}
        <div className="text-center space-y-1 pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">Target Role</div>
          <div className="text-lg font-medium text-foreground">{targetRole}</div>
        </div>

        {/* Job Match Progress - Shows score and gap to 100% */}
        <div className="bg-muted rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Job Match Progress</span>
            <span className={`text-sm font-bold ${getScoreColor(analysisData?.job_match_score || 0)}`}>
              {analysisData?.job_match_score || 0}%
            </span>
          </div>

          {/* Progress bar showing score (filled) and gap (unfilled) */}
          <div className="space-y-1">
            <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              {/* Filled portion - Your score */}
              <div
                className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${(analysisData?.job_match_score || 0) >= 80 ? 'bg-success' :
                  (analysisData?.job_match_score || 0) >= 60 ? 'bg-warning' : 'bg-error'
                  }`}
                style={{ width: `${analysisData?.job_match_score || 0}%` }}
              />
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">0%</span>
              <span className={`font-medium ${getScoreColor(analysisData?.job_match_score || 0)}`}>
                Your Score: {analysisData?.job_match_score || 0}%
              </span>
              <span className="text-muted-foreground">100%</span>
            </div>
          </div>

          {/* Gap indicator */}
          <div className="flex items-center justify-center space-x-2 text-xs">
            {(analysisData?.job_match_score || 0) >= 80 ? (
              <>
                <Icon name="CheckCircle" size={14} className="text-success" />
                <span className="text-success">Excellent match for this role!</span>
              </>
            ) : (analysisData?.job_match_score || 0) >= 60 ? (
              <>
                <Icon name="TrendingUp" size={14} className="text-warning" />
                <span className="text-warning">Good match - {100 - (analysisData?.job_match_score || 0)}% gap to close</span>
              </>
            ) : (
              <>
                <Icon name="AlertCircle" size={14} className="text-error" />
                <span className="text-error">{100 - (analysisData?.job_match_score || 0)}% skill gap - see roadmap below</span>
              </>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">
              {analysisData?.extracted_skills?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">Skills Found</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-warning">
              {analysisData?.missing_skills?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">Missing</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-success">
              {analysisData?.skills_match?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">Strengths</div>
          </div>
        </div>
        {/* Expandable Skill Details */}
        {analysisData && (
          <div className="space-y-3 pt-4 border-t border-border">
            {/* Skills Found - Expandable */}
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
                <div className="flex items-center space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground group-open:rotate-90 transition-transform" />
                  <span className="text-sm font-medium text-foreground">View All Skills Found ({analysisData?.extracted_skills?.length || 0})</span>
                </div>
              </summary>
              <div className="mt-2 ml-6 space-y-1">
                {analysisData?.extracted_skills?.map((skill, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1 px-2 bg-muted/30 rounded">
                    <span className="text-foreground">{skill.skill_name}</span>
                    <span className="text-muted-foreground capitalize">{skill.proficiency_level}</span>
                  </div>
                ))}
              </div>
            </details>

            {/* Skills Match (Strengths) - Expandable */}
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
                <div className="flex items-center space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground group-open:rotate-90 transition-transform" />
                  <span className="text-sm font-medium text-foreground">View All Matching Skills ({analysisData?.skills_match?.length || 0})</span>
                </div>
              </summary>
              <div className="mt-2 ml-6 space-y-1">
                {analysisData?.skills_match?.map((skill, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-xs py-1 px-2 bg-success/10 rounded">
                    <Icon name="CheckCircle" size={12} className="text-success" />
                    <span className="text-foreground">{skill}</span>
                  </div>
                ))}
              </div>
            </details>

            {/* Missing Skills - Expandable */}
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
                <div className="flex items-center space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground group-open:rotate-90 transition-transform" />
                  <span className="text-sm font-medium text-foreground">View All Missing Skills ({analysisData?.missing_skills?.length || 0})</span>
                </div>
              </summary>
              <div className="mt-2 ml-6 space-y-1 max-h-60 overflow-y-auto">
                {analysisData?.missing_skills?.slice(0, 50)?.map((skill, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-xs py-1 px-2 bg-warning/10 rounded">
                    <Icon name="AlertCircle" size={12} className="text-warning" />
                    <span className="text-foreground">{skill}</span>
                  </div>
                ))}
                {analysisData?.missing_skills?.length > 50 && (
                  <p className="text-xs text-muted-foreground italic">
                    ...and {analysisData.missing_skills.length - 50} more
                  </p>
                )}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchScoreCard;